'use client'

import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowUpDown, ChevronLeft, Eye, Map, MapPin, Tag, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  FilterSheetSelect,
  FilterSheetSelectContent,
  FilterSheetSelectItem,
  FilterSheetSelectTrigger,
  FilterSheetSelectValue,
} from '@/components/shared/FilterSheetSelect'
import { FilterSheetField } from '@/components/shared/FilterSheetField'
import {
  filterDropdownTriggerClassName,
  filterSheetActionButtonClassName,
} from '@/components/shared/filterSheetStyles'
import { CitySelector } from '@/components/shared/CitySelector'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { useCities } from '@/hooks/useCities'
import { useAnalytics } from '@/hooks/useAnalytics'
import type { SortingOption } from '@/components/shared/tattoo/filters/SortingFilter'
import type { ArtistSortingOption } from '@/components/shared/artist/ArtistSortingFilter'
import type { DiscoverType } from '@/hooks/useDiscoverUrlFilters'
import {
  buildAccordionRegions,
  expandLocationTokensToLaunchedCities,
  getMultiLocationFilterDisplay,
  isEffectivelyAllLocations,
} from '@/lib/locationRegions'
import { cn } from '@/lib/utils'
import { DEFAULT_DISCOVER_FILTER_DRAFT } from '@/lib/discoverFilterDefaults'
import { DiscoverFilterTypeChoices } from '@/components/discover/DiscoverFilterTypeChoices'

type FilterSheetStep = 'choose-type' | 'filters'

export interface DiscoverFilterDraft {
  discoverType: DiscoverType
  selectedLocations: string[]
  tattooSorting: SortingOption
  artistSorting: ArtistSortingOption
  includeBooked: boolean
  showDeals: boolean
  showFollowingOnly: boolean
  activeTab: 'map' | 'list'
}

interface DiscoverFilterSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  appliedFilters: DiscoverFilterDraft
  onApply: (draft: DiscoverFilterDraft) => void
  showFypOption?: boolean
  showFollowingOption?: boolean
  canChangeTattooSorting?: boolean
}

function isSheetNestedOverlayTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  return Boolean(
    target.closest('[data-radix-select-content]') ||
      target.closest('[data-radix-select-viewport]') ||
      target.closest('[data-radix-popover-content]') ||
      target.closest('[role="listbox"]'),
  )
}

function preventSheetDismissForNestedOverlay(event: Event) {
  if (isSheetNestedOverlayTarget(event.target)) {
    event.preventDefault()
  }
}

function applyDiscoverTypeChange(
  previousDraft: DiscoverFilterDraft,
  discoverType: DiscoverType,
): DiscoverFilterDraft {
  const nextDraft: DiscoverFilterDraft = {
    ...previousDraft,
    discoverType,
  }

  if (discoverType !== 'flashes') {
    nextDraft.showDeals = false
    nextDraft.includeBooked = false
  }

  if (discoverType !== 'artists') {
    nextDraft.showFollowingOnly = false
  }

  if (
    discoverType === 'tattoos' &&
    (nextDraft.tattooSorting === 'priceAsc' || nextDraft.tattooSorting === 'priceDesc')
  ) {
    nextDraft.tattooSorting = 'recent'
  }

  return nextDraft
}

export function DiscoverFilterSheet({
  open,
  onOpenChange,
  appliedFilters,
  onApply,
  showFypOption = true,
  showFollowingOption = false,
  canChangeTattooSorting = true,
}: DiscoverFilterSheetProps) {
  const { t } = useTranslation()
  const { trackEvent } = useAnalytics()
  const { cities, cityArtistCountByCity, cityCountryByCity, loading: citiesLoading } = useCities()
  const allLocationsText = t('onskin.filters.allLocations')

  const [draft, setDraft] = React.useState<DiscoverFilterDraft>(appliedFilters)
  const [draftCities, setDraftCities] = React.useState<string[]>([])
  const [sheetStep, setSheetStep] = React.useState<FilterSheetStep>('choose-type')

  const locationAccordionRegions = React.useMemo(
    () => buildAccordionRegions(cities, cityCountryByCity ?? {}),
    [cities, cityCountryByCity],
  )

  React.useEffect(() => {
    if (!open) {
      setSheetStep('choose-type')
      return
    }
    setDraft(appliedFilters)
    setSheetStep('choose-type')
    if (cities.length === 0) {
      setDraftCities([])
      return
    }
    if (isEffectivelyAllLocations(appliedFilters.selectedLocations, allLocationsText, cities)) {
      setDraftCities([])
    } else {
      setDraftCities(
        expandLocationTokensToLaunchedCities(
          appliedFilters.selectedLocations,
          allLocationsText,
          new Set(cities),
        ),
      )
    }
  }, [open, appliedFilters, cities, allLocationsText])

  const tattooSortingOptions: {
    value: SortingOption
    label: string
    showOnlyForFlashes?: boolean
    requiresFypQualification?: boolean
  }[] = [
    { value: 'trending', label: t('filters.sorting.trending', 'Trending') },
    { value: 'recent', label: t('filters.sorting.recent', 'Newest') },
    { value: 'fyp', label: t('filters.sorting.fyp', 'For You'), requiresFypQualification: true },
    {
      value: 'priceAsc',
      label: t('filters.sorting.priceAsc', 'Price: Low to High'),
      showOnlyForFlashes: true,
    },
    {
      value: 'priceDesc',
      label: t('filters.sorting.priceDesc', 'Price: High to Low'),
      showOnlyForFlashes: true,
    },
  ]

  const discoverTypeLabels: Record<DiscoverType, string> = {
    tattoos: t('navigation.tattoos', 'Tattoos'),
    flashes: t('navigation.flashes', 'Flashes'),
    artists: t('navigation.artists', 'Artists'),
  }

  const selectedDiscoverTypeLabel =
    discoverTypeLabels[draft.discoverType] ?? t('navigation.flashes', 'Flashes')

  const artistSortingOptions: { value: ArtistSortingOption; label: string }[] = [
    { value: 'discover', label: t('filters.sorting.discover', 'Discover') },
    { value: 'trending', label: t('filters.sorting.trending', 'Trending') },
    { value: 'newest', label: t('filters.sorting.recent', 'Newest') },
  ]

  const availableTattooSortingOptions = tattooSortingOptions.filter((option) => {
    if (option.showOnlyForFlashes && draft.discoverType !== 'flashes') return false
    if (option.requiresFypQualification && !showFypOption) return false
    return true
  })

  const locationDisplayText = React.useMemo(() => {
    if (cities.length === 0 || draftCities.length === 0) {
      return allLocationsText
    }
    return getMultiLocationFilterDisplay(draftCities, allLocationsText, locationAccordionRegions)
  }, [draftCities, allLocationsText, locationAccordionRegions, cities.length])

  const currentSortValue =
    draft.discoverType === 'artists' ? draft.artistSorting : draft.tattooSorting

  const currentSortLabel = React.useMemo(() => {
    if (draft.discoverType === 'artists') {
      return (
        artistSortingOptions.find((option) => option.value === draft.artistSorting)?.label ??
        t('filters.sorting.discover', 'Discover')
      )
    }

    return (
      availableTattooSortingOptions.find((option) => option.value === draft.tattooSorting)?.label ??
      t('filters.sorting.recent', 'Newest')
    )
  }, [
    artistSortingOptions,
    availableTattooSortingOptions,
    draft.artistSorting,
    draft.discoverType,
    draft.tattooSorting,
    t,
  ])

  const handleApply = () => {
    const nextLocations =
      draftCities.length === 0 || cities.length === 0 ? [] : draftCities

    const nextDraft: DiscoverFilterDraft = {
      ...draft,
      selectedLocations: nextLocations,
    }

    if (nextDraft.discoverType !== appliedFilters.discoverType) {
      trackEvent('feed_filter_changed', {
        filter_type: 'type',
        filter_value: nextDraft.discoverType,
      })
    }

    if (nextDraft.discoverType !== 'artists') {
      trackEvent('feed_sort_changed', {
        feed_type: nextDraft.discoverType,
        sort_key: nextDraft.tattooSorting,
      })
    } else {
      trackEvent('artist_filter_changed', {
        filter_type: 'sorting',
        filter_value: nextDraft.artistSorting,
      })
    }

    onApply(nextDraft)
    onOpenChange(false)
  }

  const handleClear = () => {
    setDraft((previousDraft) => ({
      ...DEFAULT_DISCOVER_FILTER_DRAFT,
      discoverType: previousDraft.discoverType,
    }))
    setDraftCities([])
  }

  const handleSelectDiscoverType = (discoverType: DiscoverType) => {
    setDraft((previousDraft) => applyDiscoverTypeChange(previousDraft, discoverType))
    setSheetStep('filters')
  }

  const handleBackToTypeSelection = () => {
    setSheetStep('choose-type')
  }

  const isChooseTypeStep = sheetStep === 'choose-type'

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="flex h-auto max-h-[85vh] flex-col p-6"
        hideCloseButton
        onPointerDownOutside={preventSheetDismissForNestedOverlay}
        onInteractOutside={preventSheetDismissForNestedOverlay}
        onFocusOutside={preventSheetDismissForNestedOverlay}
      >
        <SheetHeader className="shrink-0 space-y-0 border-b border-border/40 pb-4">
          {isChooseTypeStep ? (
            <SheetTitle className="text-xl font-semibold">
              {t('discover.filters.whatToSee', 'What are you looking for?')}
            </SheetTitle>
          ) : (
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-foreground transition-colors hover:bg-accent"
                aria-label={t('discover.filters.backToType', 'Back to content type')}
                onClick={handleBackToTypeSelection}
              >
                <ChevronLeft className="h-5 w-5" aria-hidden />
              </button>
              <SheetTitle className="flex min-w-0 flex-1 flex-col gap-0.5 text-left">
                <span className="text-xl font-semibold">{t('discover.filters.title', 'Filters')}</span>
                <span className="truncate text-sm font-normal text-muted-foreground">
                  {selectedDiscoverTypeLabel}
                </span>
              </SheetTitle>
            </div>
          )}
        </SheetHeader>

        {isChooseTypeStep ? (
          <DiscoverFilterTypeChoices onSelect={handleSelectDiscoverType} />
        ) : (
          <>
            <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto py-5">
              <FilterSheetField
                label={t('filters.location', 'Location')}
                icon={<MapPin aria-hidden />}
              >
                {citiesLoading ? (
                  <div className={cn(filterDropdownTriggerClassName, 'text-muted-foreground')}>
                    {t('common.loading', 'Loading...')}
                  </div>
                ) : (
                  <CitySelector
                    value={locationDisplayText}
                    filterCities={cities}
                    artistCountByCity={cityArtistCountByCity}
                    placeholder={allLocationsText}
                    dropdownMode="search"
                    layout="popover"
                    variant="filterSheet"
                    multiSelect
                    selectedCities={draftCities}
                    onMultiChange={setDraftCities}
                    searchPlaceholder={t('filters.searchCities', 'Search cities...')}
                  />
                )}
              </FilterSheetField>

              <FilterSheetField
                label={t('filters.sorting.title', 'Sort by')}
                icon={<ArrowUpDown aria-hidden />}
              >
                <FilterSheetSelect
                  value={currentSortValue}
                  disabled={draft.discoverType !== 'artists' && !canChangeTattooSorting}
                  onValueChange={(value) => {
                    if (draft.discoverType === 'artists') {
                      setDraft((previousDraft) => ({
                        ...previousDraft,
                        artistSorting: value as ArtistSortingOption,
                      }))
                      return
                    }
                    setDraft((previousDraft) => ({
                      ...previousDraft,
                      tattooSorting: value as SortingOption,
                    }))
                  }}
                >
                  <FilterSheetSelectTrigger>
                    <FilterSheetSelectValue>{currentSortLabel}</FilterSheetSelectValue>
                  </FilterSheetSelectTrigger>
                  <FilterSheetSelectContent position="popper">
                    {draft.discoverType === 'artists'
                      ? artistSortingOptions.map((option) => (
                          <FilterSheetSelectItem key={option.value} value={option.value}>
                            {option.label}
                          </FilterSheetSelectItem>
                        ))
                      : availableTattooSortingOptions.map((option) => (
                          <FilterSheetSelectItem key={option.value} value={option.value}>
                            {option.label}
                          </FilterSheetSelectItem>
                        ))}
                  </FilterSheetSelectContent>
                </FilterSheetSelect>
              </FilterSheetField>

              {draft.discoverType === 'artists' ? (
                <FilterSheetField
                  label={t('discover.filters.view', 'View')}
                  icon={<Map aria-hidden />}
                >
                  <FilterSheetSelect
                    value={draft.activeTab}
                    onValueChange={(value) =>
                      setDraft((previousDraft) => ({
                        ...previousDraft,
                        activeTab: value as 'map' | 'list',
                      }))
                    }
                  >
                    <FilterSheetSelectTrigger>
                      <FilterSheetSelectValue>
                        {draft.activeTab === 'map'
                          ? t('discover.filters.mapView', 'Map')
                          : t('discover.filters.listView', 'List')}
                      </FilterSheetSelectValue>
                    </FilterSheetSelectTrigger>
                    <FilterSheetSelectContent position="popper">
                      <FilterSheetSelectItem value="list">{t('discover.filters.listView', 'List')}</FilterSheetSelectItem>
                      <FilterSheetSelectItem value="map">{t('discover.filters.mapView', 'Map')}</FilterSheetSelectItem>
                    </FilterSheetSelectContent>
                  </FilterSheetSelect>
                </FilterSheetField>
              ) : null}

              {draft.discoverType === 'flashes' ? (
                <>
                  <FilterSheetField
                    label={t('discover.filters.deals', 'Deals')}
                    icon={<Tag aria-hidden />}
                  >
                    <FilterSheetSelect
                      value={draft.showDeals ? 'on' : 'off'}
                      onValueChange={(value) =>
                        setDraft((previousDraft) => ({
                          ...previousDraft,
                          showDeals: value === 'on',
                        }))
                      }
                    >
                      <FilterSheetSelectTrigger>
                        <FilterSheetSelectValue>
                          {draft.showDeals
                            ? t('discover.filters.dealsOn', 'Deals only')
                            : t('discover.filters.dealsOff', 'All flashes')}
                        </FilterSheetSelectValue>
                      </FilterSheetSelectTrigger>
                      <FilterSheetSelectContent position="popper">
                        <FilterSheetSelectItem value="off">{t('discover.filters.dealsOff', 'All flashes')}</FilterSheetSelectItem>
                        <FilterSheetSelectItem value="on">{t('discover.filters.dealsOn', 'Deals only')}</FilterSheetSelectItem>
                      </FilterSheetSelectContent>
                    </FilterSheetSelect>
                  </FilterSheetField>

                  <FilterSheetField
                    label={t('discover.filters.soldFlashes', 'Sold flashes')}
                    icon={<Eye aria-hidden />}
                  >
                    <FilterSheetSelect
                      value={draft.includeBooked ? 'show' : 'hide'}
                      onValueChange={(value) =>
                        setDraft((previousDraft) => ({
                          ...previousDraft,
                          includeBooked: value === 'show',
                        }))
                      }
                    >
                      <FilterSheetSelectTrigger>
                        <FilterSheetSelectValue>
                          {draft.includeBooked
                            ? t('discover.filters.showSold', 'Show sold')
                            : t('discover.filters.hideSold', 'Hide sold')}
                        </FilterSheetSelectValue>
                      </FilterSheetSelectTrigger>
                      <FilterSheetSelectContent position="popper">
                        <FilterSheetSelectItem value="hide">{t('discover.filters.hideSold', 'Hide sold')}</FilterSheetSelectItem>
                        <FilterSheetSelectItem value="show">{t('discover.filters.showSold', 'Show sold')}</FilterSheetSelectItem>
                      </FilterSheetSelectContent>
                    </FilterSheetSelect>
                  </FilterSheetField>
                </>
              ) : null}

              {draft.discoverType === 'artists' && showFollowingOption ? (
                <FilterSheetField
                  label={t('discover.filters.following', 'Following')}
                  icon={<Users aria-hidden />}
                >
                  <FilterSheetSelect
                    value={draft.showFollowingOnly ? 'following' : 'all'}
                    onValueChange={(value) =>
                      setDraft((previousDraft) => ({
                        ...previousDraft,
                        showFollowingOnly: value === 'following',
                      }))
                    }
                  >
                    <FilterSheetSelectTrigger>
                      <FilterSheetSelectValue>
                        {draft.showFollowingOnly
                          ? t('discover.filters.followingOnly', 'Following only')
                          : t('discover.filters.allArtists', 'All artists')}
                      </FilterSheetSelectValue>
                    </FilterSheetSelectTrigger>
                    <FilterSheetSelectContent position="popper">
                      <FilterSheetSelectItem value="all">{t('discover.filters.allArtists', 'All artists')}</FilterSheetSelectItem>
                      <FilterSheetSelectItem value="following">
                        {t('discover.filters.followingOnly', 'Following only')}
                      </FilterSheetSelectItem>
                    </FilterSheetSelectContent>
                  </FilterSheetSelect>
                </FilterSheetField>
              ) : null}
            </div>

            <div className="flex shrink-0 gap-3 border-t border-border/40 pt-5">
              <Button
                type="button"
                variant="secondary"
                className={filterSheetActionButtonClassName}
                onClick={handleClear}
              >
                {t('filters.clear', 'Clear')}
              </Button>
              <Button
                type="button"
                className={filterSheetActionButtonClassName}
                onClick={handleApply}
              >
                {t('common.apply', 'Apply')}
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}
